import { InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Checkbox, Tooltip } from 'antd';
import { Trans, useTranslation } from 'react-i18next';

import Form from './Form';

type TestType =
  | 'OCP'
  | 'LOGIC'
  | 'SJT'
  | 'MOTIVATIONAL'
  | 'PROFILEMAP'
  | 'OPERATIONAL'
  | 'INTERESTS'
  | 'NEXA';

interface TestsFormProps {
  tests: string[];
  requireTests?: boolean;
  namePrefix?: (string | number)[];
  showNexa?: boolean;
}

export interface TestsFormValues {
  testTypeList: string[];
}

const TestsForm = ({ tests, requireTests, namePrefix = [], showNexa = false }: TestsFormProps) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance();
  const testTypeList = Form.useWatch<string[] | undefined>([...namePrefix, 'testTypeList'], form);

  const testsKeyInfoRelation = {
    OCP: {
      name: t('Mapeamento Cultural'),
      description: t('Avalia o fit cultural com a empresa.'),
      averageTime: 7,
    },
    LOGIC: {
      name: t('Mental'),
      description: t('Avalia a velocidade de aprendizagem e o potencial mental.'),
      averageTime: 20,
    },
    SJT: {
      name: t('Social'),
      description: t('Avalia habilidades interpessoais.'),
      averageTime: 11,
    },
    MOTIVATIONAL: {
      name: t('Motivacional'),
      description: t('Avalia postura motivacional frente a dificuldades e incertezas.'),
      averageTime: 13,
    },
    PROFILEMAP: {
      name: t('Perfil'),
      description: t('Avalia tendências de comportamento, com base no Big Five.'),
      averageTime: 8,
    },
    OPERATIONAL: {
      name: t('Raciocínio Simplificado'),
      description: t('Avalia a habilidade de resolução de problemas lógicos e análise numérica.'),
      averageTime: 9,
    },
    INTERESTS: {
      name: t('Interesses profissionais'),
      description: t('Avalia as atividades de maior interesse do candidato.'),
      averageTime: 12,
    },
    NEXA: {
      name: t('Potencial de Liderança'),
      description: t('Avalia o potencial de liderança, com base no learning agility.'),
      averageTime: 12,
    },
  };

  const cleanedTests = tests.filter((test): test is TestType =>
    [
      'OCP',
      'LOGIC',
      'SJT',
      'MOTIVATIONAL',
      'PROFILEMAP',
      'OPERATIONAL',
      'INTERESTS',
      'NEXA',
    ].includes(test)
  );

  return (
    <>
      <Form.Item
        name={[...namePrefix, 'testTypeList']}
        rules={
          requireTests
            ? [{ min: 1, type: 'array', message: t('Selecione no mínimo um teste') }]
            : []
        }
      >
        <Checkbox.Group>
          <Form.Row>
            <Form.Col>
              <Checkbox value="OCP">{testsKeyInfoRelation['OCP']['name']}</Checkbox>
              <Tooltip
                title={
                  <span>
                    {testsKeyInfoRelation['OCP']['description']}
                    <br />
                    <strong>
                      <Trans>Duração média</Trans>:
                    </strong>{' '}
                    {testsKeyInfoRelation['OCP']['averageTime']} <Trans>minutos</Trans>
                  </span>
                }
              >
                <InfoCircleOutlined />
              </Tooltip>
            </Form.Col>
            {showNexa && (
              <Form.Col flex={1}>
                <Checkbox value="NEXA">{testsKeyInfoRelation['NEXA']['name']}</Checkbox>
                <Tooltip
                  title={
                    <span>
                      {testsKeyInfoRelation['NEXA']['description']}
                      <br />
                      <strong>
                        <Trans>Duração média</Trans>:
                      </strong>{' '}
                      {testsKeyInfoRelation['NEXA']['averageTime']} <Trans>minutos</Trans>
                    </span>
                  }
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </Form.Col>
            )}
          </Form.Row>
          <Form.Row>
            {cleanedTests.map((test: TestType) => (
              <Form.Col key={test}>
                <Checkbox value={test}>{testsKeyInfoRelation[test]['name']}</Checkbox>
                <Tooltip
                  title={
                    <span>
                      {testsKeyInfoRelation[test]['description']}
                      <br />
                      <strong>
                        <Trans>Duração média</Trans>:
                      </strong>{' '}
                      {testsKeyInfoRelation[test]['averageTime']} <Trans>minutos</Trans>
                    </span>
                  }
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </Form.Col>
            ))}
          </Form.Row>
        </Checkbox.Group>
      </Form.Item>
      {(!requireTests && testTypeList?.length === 0) ||
        (!cleanedTests.every((test) => testTypeList?.includes(test)) && (
          <>
            <Alert
              message={`${t(
                'Lembre-se: para o cálculo do Match com a posição, o candidato precisa responder os testes:'
              )} ${cleanedTests
                .map((test) => testsKeyInfoRelation[test as TestType]['name'])
                .join(', ')} ${t(
                'Se o candidato já respondeu algum teste, não se preocupe, ele será avisado ao clicar no link.'
              )}`}
              type="warning"
              showIcon
            />
            <br />
          </>
        ))}
    </>
  );
};

export default TestsForm;
